import * as React from 'react';
import type { AttendanceDailyPoint } from '@/lib/hr-api';

interface Props {
  days: AttendanceDailyPoint[];
  activeCount: number;
  windowSize?: number;
}

/**
 * Per-day "employees present" bar chart. Bar height = distinct employees
 * who punched in that day; horizontal reference line = active headcount.
 * Calendar-aligned: missing days render as zero-height ticks so weekends /
 * shutdown days are visible as gaps.
 */
export function PresenceChart({ days, activeCount, windowSize = 30 }: Props) {
  const window = React.useMemo(() => buildWindow(days, windowSize), [days, windowSize]);

  const W = 720, H = 160;
  const padL = 30, padR = 8, padT = 10, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barW = innerW / window.length;

  const yMax = Math.max(activeCount || 1, ...window.map((d) => d.present));
  const yPos = (n: number) => padT + innerH - (n / yMax) * innerH;
  const ticks = niceTicks(yMax);
  const baselineY = yPos(activeCount);

  const totalPresent = window.reduce((a, b) => a + b.present, 0);
  const presentDays = window.filter((d) => d.present > 0).length;

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-xs text-muted-foreground">
        <span>{presentDays}/{window.length} working days · avg {presentDays ? Math.round(totalPresent / presentDays) : 0} present</span>
        <span>{window[0]?.iso ?? ''} → {window[window.length - 1]?.iso ?? ''}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label="Daily attendance chart">
        {/* y-grid */}
        {ticks.map((n) => (
          <g key={n}>
            <line x1={padL} x2={W - padR} y1={yPos(n)} y2={yPos(n)} className="stroke-muted" strokeWidth={1} />
            <text x={padL - 4} y={yPos(n) + 3} textAnchor="end" className="fill-muted-foreground text-[10px]">{n}</text>
          </g>
        ))}
        {/* active-headcount baseline */}
        {activeCount > 0 && (
          <g>
            <line
              x1={padL} x2={W - padR}
              y1={baselineY} y2={baselineY}
              className="stroke-amber-500"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <text x={W - padR} y={baselineY - 3} textAnchor="end" className="fill-amber-700 text-[10px]">
              {activeCount} active
            </text>
          </g>
        )}
        {/* bars */}
        {window.map((d, i) => {
          const x = padL + i * barW + barW * 0.15;
          const w = barW * 0.7;
          const y = yPos(d.present);
          const h = padT + innerH - y;
          return (
            <rect
              key={d.iso}
              x={x} y={y} width={w} height={h}
              rx={1.5}
              className={d.present === 0 ? 'fill-muted' : 'fill-emerald-500/80'}
            >
              <title>{`${d.iso}: ${d.present} present · ${d.punches} punches`}</title>
            </rect>
          );
        })}
        {/* x-axis labels */}
        {window.map((d, i) => {
          const labelable = i === 0 || i === window.length - 1 || i % 5 === 0;
          if (!labelable) return null;
          const x = padL + i * barW + barW / 2;
          return (
            <text key={d.iso} x={x} y={H - 6} textAnchor="middle" className="fill-muted-foreground text-[10px]">
              {d.iso.slice(8)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

interface Slot { iso: string; present: number; punches: number }

function buildWindow(days: AttendanceDailyPoint[], n: number): Slot[] {
  const sorted = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const anchor = sorted[sorted.length - 1]?.date ?? todayISO();
  const byDate = new Map(days.map((d) => [d.date, d]));
  const out: Slot[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const iso = addDaysISO(anchor, -i);
    const d = byDate.get(iso);
    out.push({ iso, present: d?.present ?? 0, punches: d?.punches ?? 0 });
  }
  return out;
}

function niceTicks(max: number): number[] {
  if (max <= 0) return [0];
  const step = max <= 5 ? 1 : max <= 20 ? 5 : max <= 50 ? 10 : 20;
  const out: number[] = [];
  for (let n = 0; n <= max; n += step) out.push(n);
  return out;
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDaysISO(iso: string, n: number) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, m! - 1, d! + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
